// Sticky owner banner shown only to detected app-owner visitors.
// Tracks impression once per page-mount and click via /api/banner/track.

import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Star, X, ChevronRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useOwnerVisitor } from "@/hooks/useOwnerVisitor";
import { trackBanner } from "@/lib/api";

const BANNER_ID = "owner_premium";

const OwnerBanner = () => {
  const { lang } = useLang();
  const isNl = lang === "nl";
  const { isOwner, dismissed, dismiss } = useOwnerVisitor();
  const impressionLogged = useRef(false);

  useEffect(() => {
    if (isOwner && !dismissed && !impressionLogged.current) {
      impressionLogged.current = true;
      trackBanner(BANNER_ID, "impression");
    }
  }, [isOwner, dismissed]);

  if (!isOwner || dismissed) return null;

  const onCtaClick = () => trackBanner(BANNER_ID, "click");

  return (
    <div
      className="relative bg-gradient-to-r from-amber-500 via-amber-600 to-amber-500 text-amber-50 border-b border-amber-700/40"
      data-testid="owner-banner"
    >
      <div className="hac-container py-2.5 flex items-center gap-3 flex-wrap">
        <Star size={14} className="fill-current text-amber-100 shrink-0" />
        <span className="text-sm font-medium flex-1 min-w-0">
          {isNl ? (
            <>
              <strong className="text-white">Krijg deze week featured</strong> — Premium-listing vanaf €99/maand: instant publish, score-boost en gouden badge.
            </>
          ) : (
            <>
              <strong className="text-white">Get featured this week</strong> — Premium listing from €99/month: instant publish, score boost and gold badge.
            </>
          )}
        </span>
        <Link
          to="/premium-listing?ref=owner-banner"
          onClick={onCtaClick}
          className="inline-flex items-center gap-1 bg-white text-amber-700 text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-amber-50 transition-colors shrink-0"
          data-testid="owner-banner-cta"
        >
          {isNl ? "Bekijk Premium" : "See Premium"} <ChevronRight size={12} />
        </Link>
        <button
          onClick={dismiss}
          className="p-1 rounded-full hover:bg-amber-700/40 transition-colors shrink-0"
          aria-label={isNl ? "Sluiten" : "Dismiss"}
          data-testid="owner-banner-dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

export default OwnerBanner;
